import { useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { Loader2, Search, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatShortDate, formatTime } from "@/lib/dates";
import { SEARCH_MESSAGES } from "./queries";

interface Props {
  conversationId: string;
  onClose: () => void;
  onJumpToMessage?: (messageId: string) => void;
}

export function SearchPanel({ conversationId, onClose, onJumpToMessage }: Props) {
  const { t, i18n } = useTranslation();
  const [q, setQ] = useState("");
  const [runSearch, { data, loading }] = useLazyQuery(SEARCH_MESSAGES, {
    fetchPolicy: "network-only",
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!q.trim()) return;
    runSearch({ variables: { conversationId, q: q.trim() } });
  };

  const results = (data?.searchMessages ?? []) as Array<{
    id: string;
    body: string;
    createdAt: string;
    sender: { firstName: string };
  }>;

  return (
    // On a phone the panel is the screen while it is open: beside the conversation it left
    // about 70 px for the messages (#1495). From `md` up it is the 320 px side panel.
    <aside className="absolute inset-0 z-10 flex h-full w-full flex-col border-l border-border bg-background md:static md:z-auto md:w-[320px] md:shrink-0">
      <div className="flex h-16 items-center gap-2 border-b border-border px-4">
        <form onSubmit={onSubmit} className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("chat.search.placeholder")}
            className="h-9 w-full rounded-full border border-border bg-secondary/60 pl-9 pr-3 text-base outline-none md:text-[13px] focus:border-ring focus:bg-background focus:ring-2 focus:ring-ring/30"
          />
        </form>
        <button
          onClick={onClose}
          className="inline-flex size-9 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary focus-ring"
          aria-label={t("chat.search.close")}
        >
          <X className="size-4" />
        </button>
      </div>

      <div className="scrollbar-thin flex-1 space-y-2 overflow-y-auto p-3">
        {loading && (
          <div className="flex justify-center py-6">
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          </div>
        )}
        {!loading && data && (
          <div className="px-1 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            {t("chat.search.resultCount", { count: results.length })}
          </div>
        )}
        {!loading && data && results.length === 0 && (
          <p className="px-3 py-2 text-center text-[12px] text-muted-foreground">
            {t("chat.search.noMatches")}
          </p>
        )}
        {results.map((r) => (
          <button
            type="button"
            key={r.id}
            onClick={() => onJumpToMessage?.(r.id)}
            className="w-full rounded-xl border border-border bg-card p-3 text-left transition-colors hover:border-primary/40 hover:bg-secondary/50 focus-ring"
          >
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold">{r.sender.firstName}</span>
              <span className="text-[11px] text-muted-foreground">
                {formatShortDate(r.createdAt, i18n.language)}, {formatTime(r.createdAt, i18n.language)}
              </span>
            </div>
            <p className="mt-1 line-clamp-3 text-[13px] leading-5 text-muted-foreground">
              {r.body}
            </p>
          </button>
        ))}
      </div>
    </aside>
  );
}
